/* eslint-disable @typescript-eslint/no-unused-vars */
import { TrueTime, fromEnumAsString, isNullOrUndefined } from 'in-time-core';
import { AnalyticsTracker } from './analytics-tracker';
import { logError, logInfo } from 'in-time-logger';
import {
  GA_EVT_CTM_COOKIES_ACCEPTED,
  GA_EVT_CTM_COOKIES_DECLINED, GA_EVT_CTM_EXPLORE, GA_EVT_CTM_INITIATE_CHECKOUT,
  GA_EVT_CTM_INITIATE_PURCHASE,
  GA_EVT_CTM_PROMO_CODE_ENTERED,
  GA_EVT_CTM_PURCHASE_CANCELED, GA_EVT_CTM_PURCHASE_FAILED,
  GA_EVT_CTM_SEARCH, GA_EVT_STD_ADD_TO_CART,
  GA_EVT_STD_PURCHASE,
} from './analytics.events';
import {
  AddToCartEventArgs, AnalyticsTrackerType, CompleteRegistrationEventArgs, ContentViewEventArgs,
  InitiateCheckoutEventArgs, InitiatePurchaseEventArgs, PageViewEventArgs, PurchaseCanceledEventArgs,
  PurchaseFailedEventArgs, PurchaseCompleteEventArgs, SearchEventArgs, SignUpEventArgs,
  PromoCodeEventArgs,
  ExploreEventArgs
} from './analytics.types';
import { GtmHelper, TagManagerEntry } from '../helpers/gtm-helpers';
import { Renderer2 } from '@angular/core';
import { GA_EVT_COMPLETE_REGISTRATION } from '../../core/utils/analytics-event-names';

const TAG_MANAGER_ID = 'GTM-T78M5KG';

export class GoogleAnalyticsTracker extends AnalyticsTracker {
  private hasAcceptedCookies: boolean = false;
  private hasBeenInitialized: boolean = false;

  private tagManager: TagManagerEntry | null = null;

  public readonly $typeId: AnalyticsTrackerType = 'google-analytics';

  private constructor(
    private readonly renderer: Renderer2,
    private readonly _document: Document,
  ) { super(); }

  static create(opts: {
    renderer: Renderer2,
    document: Document,
  }): GoogleAnalyticsTracker {
    logInfo('Initialize In-Time GTM');
    return new GoogleAnalyticsTracker(opts.renderer, opts.document);
  }

  onInit(hasAcceptedCookies: boolean): void {
    this.hasAcceptedCookies = hasAcceptedCookies;

    try {
      if(this.tagManager == null) {
        const { elementId, dataLayerName } = GtmHelper.createGoogleTagManager({
          document: this._document,
          renderer: this.renderer,
          tagManagerId: TAG_MANAGER_ID,
          hasConsent: this.hasAcceptedCookies,
          useDefaultDataLayer: true,
        });

        this.tagManager = { tagManagerId: TAG_MANAGER_ID, elementId, dataLayerName };
      }

      this.hasBeenInitialized = true;
      logInfo('In-Time Google Tag Manager has been activated.');
    }
    catch(error) {
      this.hasBeenInitialized = false;
      logError('Failed to activate In-Time Google Tag Manager.', error);
    }
  }

  onDestroy(): void {
    // nothing to do here :)
  }

  onCookiesAccepted(): void {
    this.hasAcceptedCookies = true;
    this.trackConsent();
  }

  onCookiesDeclined(): void {
    this.hasAcceptedCookies = false;
    this.trackConsent();
  }

  trackPromoCode(args: PromoCodeEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_CTM_PROMO_CODE_ENTERED,
      extraData: {
        promo_code: args.promoCode,
      },
    });
  }

  trackSignUp(args: SignUpEventArgs): void {
    // skip, because it needs a totally different logic:
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=11828619753363400125-EU&client_type=gtag#sign_up
  }

  trackPageView(args: PageViewEventArgs): void {
    // skip, because there is no such thing as page_view event on google analytics
  }

  trackContentView(args: ContentViewEventArgs): void {
    // skip, because this needs a bigger refactor to make it work
    // the actual event is called view_item (view_item_list / view_cart / view_promotion)
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=11828619753363400125-EU&client_type=gtag#view_item
  }

  trackSearch(args: SearchEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_CTM_SEARCH,
      extraData: {
        search_term: args.query
      }
    });
  }

  trackExplore(args: ExploreEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_CTM_EXPLORE,
      extraData: {
        route: args.id,
        via: args.via,
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      },
    });
  }

  trackAddToCart(args: AddToCartEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_STD_ADD_TO_CART,
      ecommerce: {
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackInitiateCheckout(args: InitiateCheckoutEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_CTM_INITIATE_CHECKOUT,
      ecommerce: {
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackCompleteRegistration(args: CompleteRegistrationEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_COMPLETE_REGISTRATION,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackInitiatePurchase(args: InitiatePurchaseEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_CTM_INITIATE_PURCHASE,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackPurchaseComplete(args: PurchaseCompleteEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_STD_PURCHASE,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });

    // Deleted the extra custom events, because they are close to useless in case of google analytics
    // I hope we will delete the extra custom Meta events also
  }

  trackPurchaseFailed(args: PurchaseFailedEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_CTM_PURCHASE_FAILED,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackPurchaseCanceled(args: PurchaseCanceledEventArgs): void {
    this.track({
      analyticsEventName: GA_EVT_CTM_PURCHASE_CANCELED,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  private track({
    analyticsEventName,
    ecommerce = undefined,
    extraData = undefined,
  }: {
    analyticsEventName: string,
    ecommerce?: object,
    extraData?: object,
  }): void {
    if(!this.hasBeenInitialized ||
      !this.hasAcceptedCookies ||
      isNullOrUndefined(this.tagManager)
    ) return;

    try {
      const now = TrueTime.now();
      const hour = now.hour();
      const weekday = now.format('dddd');

      GtmHelper.logEventToGtmTracker({
        analyticsEventName,
        dataLayerName: this.tagManager?.dataLayerName,
        ecommerce,
        extraData: {
          ...extraData,
          web: true,
          creation_hour: hour,
          creation_weekday: weekday,
        }
      });

      logInfo(`Tracking Google Analytics event {${analyticsEventName}} with params: ${JSON.stringify(ecommerce, null, 2)}`);
    }
    catch(error) {
      logError(`Failed to track Google Analytics event {${analyticsEventName}} with params: ${JSON.stringify(ecommerce, null, 2)}`, error);
    }
  }

  private trackConsent(): void {
    if(!this.hasBeenInitialized) return;

    try {
      if(this.hasAcceptedCookies) {
        logInfo('Granting consent for GTM.');
      }
      else {
        logInfo('Denying consent for GTM.');
      }

      if(isNullOrUndefined(this.tagManager)) {
        return;
      }

      GtmHelper.grantUpdatedConsentToGtmTracker(this.tagManager.dataLayerName, this.hasAcceptedCookies);
    }
    catch(error) {
      logError('Failed to track GTM consent.', error);
    }
  }
}